import { useEffect, useState } from 'react';
import axios from 'axios';
import { DollarSign, Plus, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

const TASK_TYPES = [
  'image_generation', 'video_generation', 'audio_generation', 'avatar_generation',
  'reasoning', 'script_generation', 'character_generation', 'storyboard_generation',
  'prompt_generation', 'audio_transcription'
];

const emptyForm = {
  rule_name: '',
  task_type: 'image_generation',
  capability: '',
  model_id: '',
  credits_required: '',
  cost_multiplier: 1.0,
  duration_sec: '',
  resolution: '',
};

export default function PricingRules() {
  const [rules, setRules] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null); // null = create, object = edit
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchModels();
    fetchRules();
  }, []);

  const fetchModels = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModels(response.data);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/pricing-rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching pricing rules:', error);
      toast.error('Failed to fetch pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        credits_required: parseInt(formData.credits_required),
        cost_multiplier: parseFloat(formData.cost_multiplier),
        duration_sec: formData.duration_sec ? parseInt(formData.duration_sec) : null,
        model_id: formData.model_id || null,
        capability: formData.capability || null,
      };

      if (editingRule) {
        await axios.put(`${API}/pricing-rules/${editingRule.pricing_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Pricing rule updated');
      } else {
        await axios.post(`${API}/pricing-rules`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Pricing rule created');
      }
      setShowDialog(false);
      setEditingRule(null);
      setFormData(emptyForm);
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingRule ? 'update' : 'create'} rule`);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      rule_name: rule.rule_name || '',
      task_type: rule.task_type || 'image_generation',
      capability: rule.capability || '',
      model_id: rule.model_id || '',
      credits_required: rule.credits_required?.toString() || '',
      cost_multiplier: rule.cost_multiplier ?? 1.0,
      duration_sec: rule.duration_sec?.toString() || '',
      resolution: rule.resolution || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (pricingId) => {
    if (!window.confirm('Delete this pricing rule?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/pricing-rules/${pricingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const openCreate = () => {
    setEditingRule(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  // Categorize task types for display
  const taskTypeColor = (type) => {
    if (['script_generation', 'character_generation', 'storyboard_generation', 'prompt_generation', 'reasoning'].includes(type))
      return 'bg-violet-100 text-violet-800';
    if (type === 'image_generation') return 'bg-blue-100 text-blue-800';
    if (type === 'video_generation') return 'bg-purple-100 text-purple-800';
    if (type === 'audio_generation' || type === 'audio_transcription') return 'bg-emerald-100 text-emerald-800';
    return 'bg-slate-100 text-slate-800';
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-lg text-slate-600">Loading...</div></div>;

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Pricing Rules</h1>
          <p className="text-slate-600">Define credit costs for AI tasks (media generation & text/reasoning tasks)</p>
        </div>
        <Button data-testid="add-pricing-rule-btn" onClick={openCreate} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" /> Add Rule
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Rule Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Task Type</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Capability</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Model</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Credits</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Multiplier</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rules.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-slate-500">No pricing rules. Click "Add Rule" to create one.</td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.pricing_id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">{rule.rule_name}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded font-medium ${taskTypeColor(rule.task_type)}`}>
                      {rule.task_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{rule.capability || '-'}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{rule.model_name || 'Any'}</td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-green-600">{rule.credits_required}</span>
                    <span className="text-sm text-slate-500 ml-1">credits</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {rule.cost_multiplier !== 1.0 ? (
                      <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">{rule.cost_multiplier}x</span>
                    ) : (
                      <span className="text-slate-400">1.0x</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-rule-${rule.pricing_id}`} onClick={() => handleEdit(rule)} className="text-blue-600 hover:text-blue-800" title="Edit">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-rule-${rule.pricing_id}`} onClick={() => handleDelete(rule.pricing_id)} className="text-red-600 hover:text-red-800" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) setEditingRule(null); }}>
        <DialogContent className="sm:max-w-[500px]" data-testid="pricing-rule-dialog">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Pricing Rule' : 'Create Pricing Rule'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Rule Name *</Label>
                <Input data-testid="pricing-rule-name" placeholder="e.g., Standard Image Generation" value={formData.rule_name} onChange={(e) => setFormData({...formData, rule_name: e.target.value})} required />
              </div>

              <div>
                <Label>Task Type *</Label>
                <Select value={formData.task_type} onValueChange={(value) => setFormData({...formData, task_type: value, model_id: ''})}>
                  <SelectTrigger data-testid="pricing-rule-task-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__group_media" disabled className="font-semibold text-xs text-slate-400 uppercase">Media Generation</SelectItem>
                    <SelectItem value="image_generation">Image Generation</SelectItem>
                    <SelectItem value="video_generation">Video Generation</SelectItem>
                    <SelectItem value="audio_generation">Audio Generation</SelectItem>
                    <SelectItem value="avatar_generation">Avatar Generation</SelectItem>
                    <SelectItem value="__group_text" disabled className="font-semibold text-xs text-slate-400 uppercase mt-2">Text / Reasoning (Gemini)</SelectItem>
                    <SelectItem value="script_generation">Script Generation</SelectItem>
                    <SelectItem value="character_generation">Character Profile Extraction</SelectItem>
                    <SelectItem value="storyboard_generation">Storyboard / Frame Prompts</SelectItem>
                    <SelectItem value="prompt_generation">Video Prompt Enhancement</SelectItem>
                    <SelectItem value="reasoning">Reasoning / Persona Tasks</SelectItem>
                    <SelectItem value="audio_transcription">Audio Transcription</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Capability (optional)</Label>
                <Input data-testid="pricing-rule-capability" placeholder="e.g., cinematic_video, video_prompt, frame_prompts" value={formData.capability} onChange={(e) => setFormData({...formData, capability: e.target.value})} />
              </div>

              <div>
                <Label>Specific Model (optional)</Label>
                <Select value={formData.model_id || "any"} onValueChange={(value) => setFormData({...formData, model_id: value === "any" ? "" : value})}>
                  <SelectTrigger data-testid="pricing-rule-model"><SelectValue placeholder="Any model" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Model</SelectItem>
                    {models.map(model => (
                      <SelectItem key={model.model_id} value={model.model_id}>{model.model_name} ({model.model_type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Credits Required *</Label>
                  <Input data-testid="pricing-rule-credits" type="number" min="1" placeholder="100" value={formData.credits_required} onChange={(e) => setFormData({...formData, credits_required: e.target.value})} required />
                </div>
                <div>
                  <Label>Cost Multiplier</Label>
                  <Input data-testid="pricing-rule-multiplier" type="number" step="0.1" min="0.1" value={formData.cost_multiplier} onChange={(e) => setFormData({...formData, cost_multiplier: e.target.value})} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Duration (sec)</Label>
                  <Input type="number" placeholder="30" value={formData.duration_sec} onChange={(e) => setFormData({...formData, duration_sec: e.target.value})} />
                </div>
                <div>
                  <Label>Resolution</Label>
                  <Input placeholder="1080p" value={formData.resolution} onChange={(e) => setFormData({...formData, resolution: e.target.value})} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="pricing-rule-submit" type="submit" className="bg-green-600 hover:bg-green-700">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
